import {
  type QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import {
  copyCATReference,
  deleteCATReference,
  fetchCATReferenceBytes,
  getCATReferenceFile,
  getCATReferenceText,
  listCATReferences,
  searchCATReferences,
  uploadCATReference,
} from "./reference.api"
import type {
  CATReference,
  CATReferenceList,
  ReferenceSearchOrigin,
} from "./reference.types"
import { trimWhiteSpace } from "./white-space.lib"

export const referenceKeys = {
  all: ["reference"] as const,
  list: (jobId: string) => ["reference", "list", jobId] as const,
  text: (jobId: string, referenceId: string, offset: number, limit: number) =>
    ["reference", "text", jobId, referenceId, offset, limit] as const,
  search: (
    jobId: string,
    query: string,
    origin: ReferenceSearchOrigin,
    limit: number
  ) => ["reference", "search", jobId, origin, query, limit] as const,
  /** Every search of the job, whatever the query: the prefix of `search`. */
  searches: (jobId: string) => ["reference", "search", jobId] as const,
}

/**
 * The job's references. Refetched on focus and never fresh: another tab, or the project manager,
 * adds and removes references, and the list is what tells the search tab whether any text exists.
 */
export const useCATReferencesQuery = ({
  jobId,
  enabled = true,
}: {
  jobId: string
  enabled?: boolean
}) =>
  useQuery({
    queryKey: referenceKeys.list(jobId),
    queryFn: () => listCATReferences(jobId),
    enabled: enabled && !!jobId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  })

/** One page of a reference's text, in Unicode scalars. Texts do not change once read. */
export const useCATReferenceTextQuery = ({
  jobId,
  referenceId,
  offset,
  limit,
  enabled = true,
}: {
  jobId: string
  referenceId: string | null
  offset: number
  limit: number
  enabled?: boolean
}) =>
  useQuery({
    queryKey: referenceKeys.text(jobId, referenceId ?? "", offset, limit),
    queryFn: () =>
      getCATReferenceText({
        jobId,
        referenceId: referenceId ?? "",
        offset,
        limit,
      }),
    enabled: enabled && !!jobId && !!referenceId,
    staleTime: Infinity,
  })

/**
 * Passages of the job's references like `query`. A query, not a mutation: the current-segment tab
 * asks again each time the translator returns to a segment, and a cached answer for the same text
 * is as good as a new one for 30 seconds. The server's default `min_score` applies.
 */
export const useCATReferenceSearchQuery = ({
  jobId,
  query,
  origin,
  limit = 10,
  enabled = true,
}: {
  jobId: string
  query: string
  origin: ReferenceSearchOrigin
  limit?: number
  enabled?: boolean
}) =>
  useQuery({
    queryKey: referenceKeys.search(jobId, query, origin, limit),
    queryFn: () =>
      searchCATReferences({ jobId, request: { query, limit, origin } }),
    enabled: enabled && !!jobId && !!trimWhiteSpace(query),
    staleTime: 30_000,
  })

/**
 * The original bytes, on demand. The download URL is presigned for a few minutes, so it is asked
 * for at the moment of the download and never cached.
 */
export const useDownloadCATReferenceMutation = () =>
  useMutation({
    mutationFn: async ({
      jobId,
      referenceId,
    }: {
      jobId: string
      referenceId: string
    }) => {
      const { downloadUrl } = await getCATReferenceFile({ jobId, referenceId })
      return fetchCATReferenceBytes(downloadUrl)
    },
  })

/**
 * After any change to the job's references, settled either way: a refusal can come from a list
 * the page holds stale (the limit reached elsewhere, a reference already removed, the workflow
 * completed), and reading it again is what brings the panel's count and `canManage` back in line.
 * The searches go too, since a reference added or removed changes what they find.
 *
 * Returned, so the mutation stays pending until the list has been read again: the editor is busy
 * for that long, and the panel never offers an action against the list from before the change.
 */
const invalidateJobReferences = (queryClient: QueryClient, jobId: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: referenceKeys.list(jobId) }),
    queryClient.invalidateQueries({ queryKey: referenceKeys.searches(jobId) }),
  ])

/**
 * Writes a change the server confirmed into the cached list before it is read again. A read that
 * fails keeps the cached list, so without this a reference just added stayed out of sight (and
 * could be uploaded twice), and one just removed stayed on screen, until some later read worked.
 */
const updateCachedList = (
  queryClient: QueryClient,
  jobId: string,
  change: (references: CATReference[]) => CATReference[]
) =>
  queryClient.setQueryData<CATReferenceList>(
    referenceKeys.list(jobId),
    (list) => list && { ...list, references: change(list.references) }
  )

/** The list holds references oldest first, so a new one goes last. */
const appendReference =
  (reference: CATReference) => (references: CATReference[]) =>
    references.some((held) => held.id === reference.id)
      ? references
      : [...references, reference]

/** Uploads one file as a reference of the job. */
export const useUploadCATReferenceMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: uploadCATReference,
    onSuccess: (reference, { jobId }) =>
      updateCachedList(queryClient, jobId, appendReference(reference)),
    onSettled: (_data, _error, { jobId }) =>
      invalidateJobReferences(queryClient, jobId),
  })
}

/** Copies one reference of another job into the job, by the source reference's id. */
export const useCopyCATReferenceMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: copyCATReference,
    onSuccess: (reference, { jobId }) =>
      updateCachedList(queryClient, jobId, appendReference(reference)),
    onSettled: (_data, _error, { jobId }) =>
      invalidateJobReferences(queryClient, jobId),
  })
}

/** Removes one reference of the job. */
export const useDeleteCATReferenceMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteCATReference,
    onSuccess: (_data, { jobId, referenceId }) =>
      updateCachedList(queryClient, jobId, (references) =>
        references.filter((reference) => reference.id !== referenceId)
      ),
    onSettled: (_data, _error, { jobId }) =>
      invalidateJobReferences(queryClient, jobId),
  })
}
